import client from "./client";
import type {
  DatasetCatalogEntry,
  ReportDataset,
  ReportExportFormat,
  ReportRunResult,
  ReportSpec,
  RunDueReportsResult,
  SavedReport,
} from "@/types/report";

// ─────── Summary dashboards (legacy) ───────

export interface HeadcountReport {
  total_active: number;
  total_all: number;
  new_hires_90d: number;
  by_department: { name: string; count: number }[];
  by_status: Record<string, number>;
}

export interface LeaveReport {
  by_type: {
    type: string;
    total: number;
    used: number;
    pending: number;
    remaining: number;
    utilization_pct: number;
  }[];
  request_status_counts: Record<string, number>;
}

export interface TimeReport {
  total_hours: number;
  regular_hours: number;
  overtime_hours: number;
  status_counts: Record<string, number>;
}

export interface InvoiceReport {
  grand_total: number;
  by_status_count: Record<string, number>;
  by_status_total: Record<string, number>;
}

export interface ComplaintReport {
  by_severity: Record<string, number>;
  by_status: Record<string, number>;
  open: number;
  closed: number;
}

/** Time-series bundle that drives the dashboard's bento stat cards.
 *  Every field is a real read from the underlying tables — never a
 *  mock. Empty arrays / nulls mean the data wasn't extractable; the
 *  SPA renders the headline number without a chart in that case. */
export interface DashboardTrends {
  /** Last 10 monthly headcount snapshots, oldest → newest. Empty
   *  when the headcount query failed or the org has no employees. */
  headcount_trend: number[];
  /** Current count of approvals awaiting a decision. */
  approvals_pending_now: number;
  /** Trailing-7-day daily average of new approvals. The progress
   *  ring uses this as the "max" so the fill shows today vs a
   *  typical day rather than today vs an arbitrary number. */
  approvals_avg_7d: number;
  /** Currently open job postings. */
  open_positions_now: number;
  /** Last 7 weeks of new postings (oldest → newest), one entry per
   *  week, for the mini-bar viz. */
  open_positions_weekly: number[];
  /** Fraction (0–1) of active employees who are fully compliant.
   *  Null when there are no active employees to evaluate. */
  compliance_pct: number | null;
  /** Active-employee headcount used as the compliance denominator. */
  compliance_total: number;
}

export interface OverviewReport {
  headcount: HeadcountReport;
  leave: LeaveReport;
  time: TimeReport;
  invoices: InvoiceReport;
  complaints: ComplaintReport;
  trends?: DashboardTrends;
}

export async function getOverview(): Promise<OverviewReport> {
  const { data } = await client.get("/reports/overview");
  return data;
}

// ─────── Section 5A pre-built reports ───────

export interface ReportFilters {
  start_date?: string;
  end_date?: string;
  department?: string;
}

export interface TurnoverReport {
  window: { start: string; end: string };
  total_terminations: number;
  avg_headcount: number;
  turnover_rate_pct: number;
  by_department: { name: string; count: number }[];
  by_month: { month: string; count: number }[];
  by_reason: { reason: string; count: number }[];
}

export interface TimeToHireReport {
  window: { start: string; end: string };
  total_hires: number;
  avg_days: number;
  median_days: number;
  min_days: number;
  max_days: number;
  by_department: { name: string; hires: number; avg_days: number }[];
}

export interface DiversityReport {
  total: number;
  by_department: { name: string; count: number }[];
  by_employment_type: { type: string; count: number }[];
  by_tenure: { bucket: string; count: number }[];
  by_location: { location: string; count: number }[];
  by_gender: { gender: string; count: number }[];
  notes: string[];
}

export interface CompensationReport {
  overall: {
    count: number;
    min: number;
    max: number;
    avg: number;
    median: number;
  };
  by_department: {
    name: string;
    count: number;
    min: number;
    max: number;
    avg: number;
    median: number;
  }[];
  by_title: {
    title: string;
    count: number;
    min: number;
    max: number;
    avg: number;
    median: number;
    band_min: number | null;
    band_mid: number | null;
    band_max: number | null;
  }[];
  bands_covered: number;
}

export interface ComplianceReport {
  total_assignments: number;
  overdue: number;
  by_status: { status: string; count: number }[];
  by_category: {
    category: string;
    counts: { status: string; count: number }[];
  }[];
  requirements: {
    id: string;
    name: string;
    category: string;
    total: number;
    completed: number;
    completion_rate_pct: number;
  }[];
}

export interface OnboardingReport {
  window: { start: string; end: string };
  total_instances: number;
  completion_rate_pct: number;
  task_completion_rate_pct: number;
  avg_days_to_complete: number;
  by_status: { status: string; count: number }[];
  task_rollup: { total_tasks: number; completed_tasks: number };
}

export async function getTurnoverReport(
  filters: ReportFilters = {},
): Promise<TurnoverReport> {
  const { data } = await client.get("/reports/turnover", { params: filters });
  return data;
}

export async function getTimeToHireReport(
  filters: ReportFilters = {},
): Promise<TimeToHireReport> {
  const { data } = await client.get("/reports/time-to-hire", {
    params: filters,
  });
  return data;
}

export async function getDiversityReport(
  filters: ReportFilters = {},
): Promise<DiversityReport> {
  const { data } = await client.get("/reports/diversity", { params: filters });
  return data;
}

export async function getCompensationReport(
  filters: ReportFilters = {},
): Promise<CompensationReport> {
  const { data } = await client.get("/reports/compensation", {
    params: filters,
  });
  return data;
}

export async function getComplianceReport(
  filters: ReportFilters = {},
): Promise<ComplianceReport> {
  const { data } = await client.get("/reports/compliance", {
    params: filters,
  });
  return data;
}

export async function getOnboardingReport(
  filters: ReportFilters = {},
): Promise<OnboardingReport> {
  const { data } = await client.get("/reports/onboarding", {
    params: filters,
  });
  return data;
}

export type PrebuiltReportKey =
  | "turnover"
  | "time-to-hire"
  | "diversity"
  | "compensation"
  | "leave-usage"
  | "compliance"
  | "onboarding"
  | "headcount";

export async function exportPrebuiltReport(
  key: PrebuiltReportKey,
  filters: ReportFilters = {},
): Promise<{ blob: Blob; filename: string }> {
  const response = await client.get(`/reports/${key}`, {
    params: { ...filters, format: "csv" },
    responseType: "blob",
  });
  return {
    blob: response.data,
    filename:
      filenameFromHeaders(response.headers) ?? `${key}.csv`,
  };
}

// ─────── Custom report builder ───────

export async function getDatasetCatalog(): Promise<DatasetCatalogEntry[]> {
  const { data } = await client.get("/reports/datasets");
  return data.datasets;
}

export async function runCustomReport(
  dataset: ReportDataset,
  spec: ReportSpec,
): Promise<ReportRunResult> {
  const { data } = await client.post("/reports/run", { dataset, spec });
  return data;
}

/**
 * Export an ad-hoc report and trigger a browser download.
 * Returns the blob URL so callers can override the naming if they want.
 */
export async function exportCustomReport(
  dataset: ReportDataset,
  spec: ReportSpec,
  format: ReportExportFormat,
  title?: string,
): Promise<{ blob: Blob; filename: string }> {
  const response = await client.post(
    "/reports/export",
    { dataset, spec, format, title },
    { responseType: "blob" },
  );
  return {
    blob: response.data,
    filename: filenameFromHeaders(response.headers) ?? defaultFilename(dataset, format),
  };
}

// ─────── Saved reports ───────

export interface SavedReportCreateBody {
  name: string;
  description?: string;
  dataset: ReportDataset;
  spec: ReportSpec;
  is_scheduled?: boolean;
  schedule_cadence?: "NONE" | "DAILY" | "WEEKLY" | "MONTHLY";
  schedule_day_of_week?: number;
  schedule_day_of_month?: number;
  recipients?: string[];
  export_format?: ReportExportFormat;
}

export interface SavedReportUpdateBody {
  name?: string;
  description?: string;
  spec?: ReportSpec;
  is_scheduled?: boolean;
  schedule_cadence?: "NONE" | "DAILY" | "WEEKLY" | "MONTHLY";
  schedule_day_of_week?: number;
  schedule_day_of_month?: number;
  recipients?: string[];
  export_format?: ReportExportFormat;
}

export async function listSavedReports(): Promise<{ items: SavedReport[] }> {
  const { data } = await client.get("/reports/saved");
  return data;
}

export async function getSavedReport(id: string): Promise<SavedReport> {
  const { data } = await client.get(`/reports/saved/${id}`);
  return data;
}

export async function createSavedReport(
  body: SavedReportCreateBody,
): Promise<SavedReport> {
  const { data } = await client.post("/reports/saved", body);
  return data;
}

export async function updateSavedReport(
  id: string,
  body: SavedReportUpdateBody,
): Promise<SavedReport> {
  const { data } = await client.put(`/reports/saved/${id}`, body);
  return data;
}

export async function deleteSavedReport(id: string): Promise<void> {
  await client.delete(`/reports/saved/${id}`);
}

export async function runSavedReport(id: string): Promise<ReportRunResult> {
  const { data } = await client.post(`/reports/saved/${id}/run`);
  return data;
}

export async function exportSavedReport(
  id: string,
  format?: ReportExportFormat,
): Promise<{ blob: Blob; filename: string }> {
  const params: Record<string, string> = {};
  if (format) params.format = format;
  const response = await client.post(
    `/reports/saved/${id}/export`,
    null,
    { params, responseType: "blob" },
  );
  return {
    blob: response.data,
    filename:
      filenameFromHeaders(response.headers) ??
      `saved-report${formatExt(format ?? "PDF")}`,
  };
}

export async function runDueSavedReports(
  asOf?: string,
): Promise<RunDueReportsResult> {
  const params: Record<string, string> = {};
  if (asOf) params.as_of = asOf;
  const { data } = await client.post(
    "/reports/saved/run-due",
    null,
    { params },
  );
  return data;
}

// ─────── Utilities ───────

function filenameFromHeaders(
  headers: Record<string, string> | unknown,
): string | null {
  const disp =
    (headers as Record<string, string> | undefined)?.["content-disposition"];
  if (!disp) return null;
  const m = /filename="?([^"]+)"?/.exec(disp);
  return m ? m[1] : null;
}

function defaultFilename(dataset: ReportDataset, fmt: ReportExportFormat) {
  return `report-${dataset.toLowerCase()}${formatExt(fmt)}`;
}

function formatExt(fmt: ReportExportFormat) {
  switch (fmt) {
    case "CSV":
      return ".csv";
    case "XLSX":
      return ".xlsx";
    case "PDF":
      return ".pdf";
  }
}

/**
 * Trigger a browser download from a blob. Works in both Chrome/Safari/Firefox.
 */
export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser a tick to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
